import type { PatronCategory, PatronCategoryRulesInput, PatronCategoryType } from "@/api/types";
import { formatEuros, plural } from "./loanRules";

export const CATEGORY_TYPE_LABELS: Record<PatronCategoryType, string> = {
  individual: "Individuel",
  institutional: "Collectivité",
};

/** Server-side defaults of a new category (PatronCategoryResource). */
export const DEFAULT_CATEGORY_RULES: PatronCategoryRulesInput = {
  defaultLoanDays: 21,
  maxLoans: 10,
  maxReservations: 5,
  maxRenewals: 2,
  renewalWindowDays: 7,
  finePerDayInCents: 20,
  maxFineInCents: 1000,
};

export const CODE_MAX_LENGTH = 20;
const CODE_PATTERN = /^[A-Z0-9_-]+$/;

/**
 * Suggested code for a category name: "Jeune (12-17 ans)" becomes "JEUNE_12-17_ANS".
 * Accents are dropped because the API only accepts unaccented letters.
 */
export function codeFromName(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9-]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, CODE_MAX_LENGTH)
    .replace(/_+$/, "");
}

export interface IdentityDraft {
  name: string;
  code: string;
}

export type IdentityErrors = Partial<Record<keyof IdentityDraft, string | undefined>>;

/** Mirrors the API constraints, plus code uniqueness against the categories already loaded. */
export function validateIdentity(
  draft: IdentityDraft,
  categories: PatronCategory[],
  currentId: string | null
): IdentityErrors {
  const errors: IdentityErrors = {};
  const name = draft.name.trim();
  const code = draft.code.trim().toUpperCase();

  if (name.length < 2) errors.name = "Au moins 2 caractères";
  else if (name.length > 100) errors.name = "100 caractères maximum";

  if (code.length < 2) errors.code = "Au moins 2 caractères";
  else if (code.length > CODE_MAX_LENGTH)
    errors.code = `${String(CODE_MAX_LENGTH)} caractères maximum`;
  else if (!CODE_PATTERN.test(code))
    errors.code = "Lettres sans accent, chiffres, « - » et « _ » uniquement";
  else if (categories.some((c) => c.id !== currentId && c.code.toUpperCase() === code))
    errors.code = "Déjà utilisé par une autre catégorie";

  return errors;
}

/** Places a new category after the existing ones in every list. */
export function nextSortOrder(categories: PatronCategory[]): number {
  return categories.reduce((max, c) => Math.max(max, c.sortOrder + 1), 0);
}

export function pickRules(category: PatronCategoryRulesInput): PatronCategoryRulesInput {
  return {
    defaultLoanDays: category.defaultLoanDays,
    maxLoans: category.maxLoans,
    maxReservations: category.maxReservations,
    maxRenewals: category.maxRenewals,
    renewalWindowDays: category.renewalWindowDays,
    finePerDayInCents: category.finePerDayInCents,
    maxFineInCents: category.maxFineInCents,
  };
}

/**
 * The rules as a patron would read them. Each line is null while the values
 * it needs are not valid yet, so the preview never shows a half-typed number.
 */
export function describeRules(rules: Partial<PatronCategoryRulesInput>): {
  loans: string | null;
  renewals: string | null;
  reservations: string | null;
  fines: string | null;
} {
  const {
    defaultLoanDays,
    maxLoans,
    maxRenewals,
    renewalWindowDays,
    maxReservations,
    finePerDayInCents,
    maxFineInCents,
  } = rules;

  const loans =
    defaultLoanDays !== undefined && maxLoans !== undefined
      ? `Jusqu'à ${plural(maxLoans, "document")} pendant ${plural(defaultLoanDays, "jour")}`
      : null;

  let renewals: string | null = null;
  if (maxRenewals === 0) renewals = "Pas de prolongation";
  else if (maxRenewals !== undefined && renewalWindowDays !== undefined)
    renewals = `${maxRenewals > 1 ? `${String(maxRenewals)} prolongations` : "1 prolongation"}, ${
      renewalWindowDays === 0
        ? "le jour de l'échéance"
        : `dès ${plural(renewalWindowDays, "jour")} avant l'échéance`
    }`;

  let reservations: string | null = null;
  if (maxReservations === 0) reservations = "Pas de réservation";
  else if (maxReservations !== undefined)
    reservations = `Jusqu'à ${plural(maxReservations, "réservation")} en cours`;

  let fines: string | null = null;
  if (finePerDayInCents === 0) fines = "Aucune amende de retard";
  else if (finePerDayInCents !== undefined && maxFineInCents !== undefined)
    fines = `${formatEuros(finePerDayInCents)} par jour de retard, ${
      maxFineInCents === 0
        ? "sans plafond"
        : `plafonnés à ${formatEuros(maxFineInCents)} par document`
    }`;

  return { loans, renewals, reservations, fines };
}
